/**
 * HorizontalSunTimesOverlay Component
 * Visual overlay showing VFR daylight hours for horizontal calendar views
 * Displays as a horizontal band across the time axis
 */

import React from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import type { SunTimesDay } from '@/api/types/calendars.types';
import './HorizontalSunTimesOverlay.css';

interface HorizontalSunTimesOverlayProps {
  /** Sun times data mapped by date (YYYY-MM-DD) */
  sunTimesMap: Record<string, SunTimesDay>;
  /** Days being displayed */
  days: Dayjs[];
  /** Hour resolution (1, 2, or 4 hours per column) */
  hourResolution: number;
  /** Optional: show civil twilight instead of just sunrise/sunset */
  showTwilight?: boolean;
}

const COLUMN_WIDTH = 120; // pixels per time column (must match HorizontalView)

/**
 * Calculate horizontal position and width for daylight overlay
 * Returns position relative to the start of the period
 */
const calculateHorizontalDaylightPosition = (
  sunTimesMap: Record<string, SunTimesDay>,
  days: Dayjs[],
  hourResolution: number,
  showTwilight: boolean,
  parse: (date: string) => dayjs.Dayjs
): Array<{ left: number; width: number; date: string }> => {
  const periodStart = days[0];
  const segments: Array<{ left: number; width: number; date: string }> = [];

  days.forEach((day) => {
    const dateKey = day.format('YYYY-MM-DD');
    const sunTimes = sunTimesMap[dateKey];

    if (!sunTimes) return;

    // Use civil twilight for VFR planning, or sunrise/sunset for visual daylight
    const startTime = showTwilight
      ? parse(sunTimes.civil_twilight_begin)
      : parse(sunTimes.sunrise);
    const endTime = showTwilight
      ? parse(sunTimes.civil_twilight_end)
      : parse(sunTimes.sunset);

    // Calculate offset from period start in minutes
    const startOffsetMinutes = startTime.diff(periodStart, 'minute');
    const endOffsetMinutes = endTime.diff(periodStart, 'minute');

    // Convert to pixels (each column represents hourResolution hours)
    const minutesPerColumn = hourResolution * 60;
    const pixelsPerMinute = COLUMN_WIDTH / minutesPerColumn;

    const left = startOffsetMinutes * pixelsPerMinute;
    const width = (endOffsetMinutes - startOffsetMinutes) * pixelsPerMinute;

    segments.push({ left, width, date: dateKey });
  });

  return segments;
};

export const HorizontalSunTimesOverlay: React.FC<
  HorizontalSunTimesOverlayProps
> = ({ sunTimesMap, days, hourResolution, showTwilight = true }) => {
  const { parse } = useUserTimezone();

  const daylightSegments = calculateHorizontalDaylightPosition(
    sunTimesMap,
    days,
    hourResolution,
    showTwilight,
    parse
  );

  return (
    <>
      {daylightSegments.map((segment, index) => {
        const sunTimes = sunTimesMap[segment.date];
        const dayLength = Math.floor(sunTimes.day_length / 3600);
        const dayLengthMinutes = Math.floor((sunTimes.day_length % 3600) / 60);

        return (
          <div
            key={index}
            className="horizontal-sun-times-overlay"
            style={{
              left: `${segment.left}px`,
              width: `${segment.width}px`,
            }}
            title={`VFR Daylight: ${dayLength}h ${dayLengthMinutes}m`}
          >
            {/* Optional: Add sunrise/sunset markers if width is sufficient */}
            {segment.width > 100 && (
              <div className="horizontal-sun-times-info">
                {dayLength}h {dayLengthMinutes}m
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};
